/**
 * StudentProgressTable Component
 * 
 * Displays a table of student progress for a course.
 * Columns: Nombre, Email, Progreso, Última Actividad
 * Sorted by progress (lowest first by default).
 * Includes "Exportar CSV" button.
 * 
 * Follows the Instituto Valparaíso design system (dark theme, gradients, Framer Motion).
 * 
 * Requirements: 8.4, 8.5
 */

import { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import PropTypes from 'prop-types';
import { 
  Download, 
  Users, 
  ArrowUpDown, 
  ArrowUp, 
  ArrowDown,
  AlertCircle,
  Search,
  X
} from 'lucide-react';
import ProgressBar from './ProgressBar';

/**
 * Format date to Spanish locale string
 * @param {string|null} dateString - ISO date string or null
 * @returns {string} Formatted date or placeholder
 */
const formatDate = (dateString) => {
  if (!dateString) return 'Sin actividad';
  
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-CL', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return 'Sin actividad';
  }
};

/**
 * StudentProgressTable Component
 * 
 * @param {Array} students - Array of student progress data
 * @param {boolean} loading - Whether data is being loaded
 * @param {string} error - Error message if data fetch failed
 * @param {Function} onExportCSV - Callback to export data as CSV
 * @param {boolean} exportLoading - Whether CSV export is in progress
 * @param {string} courseName - Name of the course (for CSV filename)
 * @param {Function} onRefresh - Callback to refresh student data
 * @param {string} className - Additional CSS classes
 */
export default function StudentProgressTable({
  students = [],
  loading = false,
  error = null,
  onExportCSV = null,
  exportLoading = false,
  courseName = '',
  onRefresh = null,
  className = '',
}) {
  // Sort state: 'progress' | 'name' | 'email' | 'lastActivity'
  const [sortBy, setSortBy] = useState('progress');
  const [sortOrder, setSortOrder] = useState('asc'); // 'asc' | 'desc'
  
  // Search/filter state
  const [searchQuery, setSearchQuery] = useState('');

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.05,
        delayChildren: 0.1,
      },
    },
  };

  const rowVariants = {
    hidden: { opacity: 0, x: -10 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.3 },
    },
  };

  /**
   * Handle column header click for sorting
   */
  const handleSort = (column) => {
    if (sortBy === column) {
      // Toggle order if same column
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // New column, default to ascending (except progress which defaults to asc for lowest first)
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  /**
   * Get sort icon for column header
   */
  const getSortIcon = (column) => {
    if (sortBy !== column) {
      return <ArrowUpDown className="w-3 h-3 text-white/30" />;
    }
    return sortOrder === 'asc' 
      ? <ArrowUp className="w-3 h-3 text-blue-400" />
      : <ArrowDown className="w-3 h-3 text-blue-400" />;
  };

  /**
   * Filter and sort students
   */
  const filteredAndSortedStudents = useMemo(() => {
    let result = [...students];

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (student) =>
          student.name?.toLowerCase().includes(query) ||
          student.email?.toLowerCase().includes(query)
      );
    }

    // Sort
    result.sort((a, b) => {
      let comparison = 0;

      switch (sortBy) {
        case 'name':
          comparison = (a.name || '').localeCompare(b.name || '');
          break;
        case 'email':
          comparison = (a.email || '').localeCompare(b.email || '');
          break;
        case 'progress':
          comparison = (a.progress || 0) - (b.progress || 0);
          break;
        case 'lastActivity':
          const dateA = a.lastActivityDate ? new Date(a.lastActivityDate).getTime() : 0;
          const dateB = b.lastActivityDate ? new Date(b.lastActivityDate).getTime() : 0;
          comparison = dateA - dateB;
          break;
        default:
          comparison = 0;
      }

      return sortOrder === 'asc' ? comparison : -comparison;
    });

    return result;
  }, [students, searchQuery, sortBy, sortOrder]);

  /**
   * Calculate summary stats
   */
  const stats = useMemo(() => {
    if (students.length === 0) return { total: 0, avgProgress: 0 };
    
    const total = students.length;
    const avgProgress = students.reduce((sum, s) => sum + (s.progress || 0), 0) / total;
    
    return { total, avgProgress: Math.round(avgProgress) };
  }, [students]);

  return (
    <div className={`${className}`}>
      {/* Header with title and actions */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h3 className="text-xl font-semibold text-white flex items-center gap-2">
            <Users className="w-5 h-5 text-blue-400" />
            Progreso de Estudiantes
          </h3>
          {!loading && !error && students.length > 0 && (
            <p className="text-sm text-white/50 mt-1">
              {stats.total} estudiantes · Promedio: {stats.avgProgress}%
            </p>
          )}
        </div>

        <div className="flex items-center gap-3">
          {/* Refresh button */}
          {onRefresh && !loading && (
            <motion.button
              onClick={onRefresh}
              className="px-4 py-2 text-sm text-white/60 hover:text-white
                         bg-white/5 hover:bg-white/10 rounded-lg
                         border border-white/10 hover:border-white/20
                         transition-all"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              Actualizar
            </motion.button>
          )}

          {/* Export CSV button */}
          {onExportCSV && !loading && !error && students.length > 0 && (
            <motion.button
              onClick={onExportCSV}
              disabled={exportLoading}
              className="inline-flex items-center gap-2 px-4 py-2
                         bg-gradient-to-r from-blue-500 to-cyan-500
                         hover:from-blue-600 hover:to-cyan-600
                         disabled:from-blue-500/50 disabled:to-cyan-500/50
                         text-white text-sm font-medium rounded-lg
                         shadow-lg shadow-blue-500/20
                         transition-all disabled:cursor-not-allowed"
              whileHover={!exportLoading ? { scale: 1.02 } : {}}
              whileTap={!exportLoading ? { scale: 0.98 } : {}}
            >
              {exportLoading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Exportando...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4" />
                  Exportar CSV
                </>
              )}
            </motion.button>
          )}
        </div>
      </div>

      {/* Search input */}
      {!loading && !error && students.length > 0 && (
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Buscar por nombre o email..."
            className="w-full pl-10 pr-10 py-2.5 
                       bg-white/5 border border-white/10 
                       hover:border-white/20 focus:border-blue-500/50
                       text-white placeholder-white/40
                       rounded-lg text-sm
                       focus:outline-none focus:ring-1 focus:ring-blue-500/30
                       transition-all"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 
                         text-white/40 hover:text-white/60 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <motion.div
          className="flex flex-col items-center justify-center py-12"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          <div className="w-10 h-10 border-4 border-blue-500/30 border-t-blue-500 rounded-full animate-spin mb-4" />
          <p className="text-white/60 text-sm">Cargando estudiantes...</p>
        </motion.div>
      )}

      {/* Error State */}
      {error && !loading && (
        <motion.div
          className="flex flex-col items-center justify-center py-12 text-center"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
        >
          <div className="p-3 rounded-full bg-red-500/20 mb-4">
            <AlertCircle className="w-6 h-6 text-red-400" />
          </div>
          <h4 className="text-lg font-semibold text-white mb-2">
            Error al cargar estudiantes
          </h4>
          <p className="text-white/60 text-sm mb-4 max-w-sm">{error}</p>
          {onRefresh && (
            <motion.button
              onClick={onRefresh}
              className="px-5 py-2 bg-gradient-to-r from-blue-500 to-cyan-500 
                         hover:from-blue-600 hover:to-cyan-600
                         text-white font-medium rounded-lg text-sm
                         shadow-lg shadow-blue-500/30 transition-all"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
            >
              Reintentar
            </motion.button>
          )}
        </motion.div>
      )}

      {/* Empty State */}
      {!loading && !error && students.length === 0 && (
        <motion.div
          className="flex flex-col items-center justify-center py-12 text-center"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
        >
          <div className="p-3 rounded-full bg-blue-500/20 mb-4">
            <Users className="w-6 h-6 text-blue-400" />
          </div>
          <h4 className="text-lg font-semibold text-white mb-2">
            No hay estudiantes inscritos
          </h4>
          <p className="text-white/60 text-sm max-w-sm">
            Este curso aún no tiene estudiantes inscritos.
          </p>
        </motion.div>
      )}

      {/* No results from search */}
      {!loading && !error && students.length > 0 && filteredAndSortedStudents.length === 0 && (
        <motion.div
          className="flex flex-col items-center justify-center py-12 text-center"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
        >
          <div className="p-3 rounded-full bg-amber-500/20 mb-4">
            <Search className="w-6 h-6 text-amber-400" />
          </div>
          <h4 className="text-lg font-semibold text-white mb-2">
            Sin resultados
          </h4>
          <p className="text-white/60 text-sm max-w-sm">
            No se encontraron estudiantes que coincidan con "{searchQuery}"
          </p>
          <motion.button
            onClick={() => setSearchQuery('')}
            className="mt-4 px-4 py-2 text-sm text-blue-400 hover:text-blue-300
                       bg-blue-500/10 hover:bg-blue-500/20 rounded-lg
                       transition-all"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            Limpiar búsqueda
          </motion.button>
        </motion.div>
      )}

      {/* Table */}
      {!loading && !error && filteredAndSortedStudents.length > 0 && (
        <div className="overflow-x-auto rounded-xl border border-white/10">
          <table className="w-full">
            {/* Table Header */}
            <thead>
              <tr className="bg-white/5 border-b border-white/10">
                <th className="text-left px-4 py-3">
                  <button
                    onClick={() => handleSort('name')}
                    className="flex items-center gap-2 text-sm font-semibold text-white/80 
                               hover:text-white transition-colors"
                  >
                    Nombre
                    {getSortIcon('name')}
                  </button>
                </th>
                <th className="text-left px-4 py-3">
                  <button
                    onClick={() => handleSort('email')}
                    className="flex items-center gap-2 text-sm font-semibold text-white/80 
                               hover:text-white transition-colors"
                  >
                    Email
                    {getSortIcon('email')}
                  </button>
                </th>
                <th className="text-left px-4 py-3 min-w-[180px]">
                  <button
                    onClick={() => handleSort('progress')}
                    className="flex items-center gap-2 text-sm font-semibold text-white/80 
                               hover:text-white transition-colors"
                  >
                    Progreso
                    {getSortIcon('progress')}
                  </button>
                </th>
                <th className="text-left px-4 py-3">
                  <button
                    onClick={() => handleSort('lastActivity')}
                    className="flex items-center gap-2 text-sm font-semibold text-white/80 
                               hover:text-white transition-colors"
                  >
                    Última Actividad
                    {getSortIcon('lastActivity')}
                  </button>
                </th>
              </tr>
            </thead>

            {/* Table Body */}
            <motion.tbody
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              {filteredAndSortedStudents.map((student, index) => (
                <motion.tr
                  key={student.id || student.email || index}
                  variants={rowVariants}
                  className="border-b border-white/5 last:border-b-0
                             hover:bg-white/5 transition-colors"
                >
                  {/* Name */}
                  <td className="px-4 py-3">
                    <span className="text-sm font-medium text-white">
                      {student.name || 'Sin nombre'}
                    </span>
                  </td>

                  {/* Email */}
                  <td className="px-4 py-3">
                    <span className="text-sm text-white/70">
                      {student.email || '-'}
                    </span>
                  </td>

                  {/* Progress */}
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <ProgressBar 
                        progress={student.progress || 0} 
                        size="sm" 
                        showPercentage={false}
                        className="flex-1 max-w-[120px]"
                      />
                      <span className="text-sm font-medium text-white/80 min-w-[40px]">
                        {Math.round(student.progress || 0)}%
                      </span>
                    </div>
                  </td>

                  {/* Last Activity */}
                  <td className="px-4 py-3">
                    <span className="text-sm text-white/60">
                      {formatDate(student.lastActivityDate)}
                    </span>
                  </td>
                </motion.tr>
              ))}
            </motion.tbody>
          </table>
        </div>
      )}

      {/* Results count */}
      {!loading && !error && students.length > 0 && searchQuery && filteredAndSortedStudents.length > 0 && (
        <p className="text-xs text-white/40 mt-3 text-right">
          Mostrando {filteredAndSortedStudents.length} de {students.length} estudiantes
        </p>
      )}
    </div>
  );
}

StudentProgressTable.propTypes = {
  students: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      email: PropTypes.string,
      progress: PropTypes.number,
      lastActivityDate: PropTypes.string,
    })
  ),
  loading: PropTypes.bool,
  error: PropTypes.string,
  onExportCSV: PropTypes.func,
  exportLoading: PropTypes.bool,
  courseName: PropTypes.string,
  onRefresh: PropTypes.func,
  className: PropTypes.string,
};
